import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { Image, Loader2, Plus, Trash2, Upload, ArrowUp, ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { categoriesAPI, companyAPI, productsAPI } from "@/lib/api";
import type { Category, Product } from "@/types";
import type { StorefrontBanner, StorefrontBannerLinkType } from "@/features/pos/storefront-banners";

type Props = {
	banners: StorefrontBanner[];
	onChange: Dispatch<SetStateAction<StorefrontBanner[]>>;
	editing: boolean;
	onUploadStart?: () => void;
	onUploadEnd?: () => void;
};

function newBannerId(): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
	return `banner-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveImageUrl(value: string): string {
	const url = value.trim();
	if (!url || /^(data:|blob:|https?:\/\/)/i.test(url)) return url;
	const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api\/?$/, "");
	return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
}

function toDateInput(value: string | null): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
	return local.toISOString().slice(0, 10);
}

function fromDateInput(value: string, endOfDay: boolean): string | null {
	if (!value) return null;
	return new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`).toISOString();
}

function productRows(response: unknown): Product[] {
	if (Array.isArray(response)) return response as Product[];
	if (response && typeof response === "object") {
		const products = (response as { products?: unknown }).products;
		if (Array.isArray(products)) return products as Product[];
	}
	return [];
}

async function loadAllActiveProducts(): Promise<Product[]> {
	const pageSize = 500;
	const first = await productsAPI.getAll({ limit: pageSize, page: 1, status: "active" });
	const rows = productRows(first);
	const total =
		first && typeof first === "object" ? Number((first as { total?: unknown }).total ?? rows.length) : rows.length;
	const pageCount = Math.ceil(total / pageSize);
	if (pageCount <= 1) return rows;
	const remaining = await Promise.all(
		Array.from({ length: pageCount - 1 }, (_, index) =>
			productsAPI.getAll({ limit: pageSize, page: index + 2, status: "active" }),
		),
	);
	return [...rows, ...remaining.flatMap(productRows)];
}

function categoryRows(response: unknown): Category[] {
	if (Array.isArray(response)) return (response as Category[]).filter(category => category.status === "active");
	if (response && typeof response === "object") {
		const data = (response as { data?: unknown }).data;
		if (Array.isArray(data)) return (data as Category[]).filter(category => category.status === "active");
	}
	return [];
}

export function StorefrontBannersSettings({ banners, onChange, editing, onUploadStart, onUploadEnd }: Props) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [products, setProducts] = useState<Product[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [loadingTargets, setLoadingTargets] = useState(false);
	const [uploadingIds, setUploadingIds] = useState<string[]>([]);

	useEffect(() => {
		if (!editing) return;
		let cancelled = false;
		setLoadingTargets(true);
		Promise.all([loadAllActiveProducts(), categoriesAPI.getAll({ limit: 1000, page: 1, status: "active" })])
			.then(([productResponse, categoryResponse]) => {
				if (cancelled) return;
				setProducts(productResponse);
				setCategories(categoryRows(categoryResponse));
			})
			.catch(() => {
				if (!cancelled) {
					setProducts([]);
					setCategories([]);
				}
			})
			.finally(() => {
				if (!cancelled) setLoadingTargets(false);
			});
		return () => {
			cancelled = true;
		};
	}, [editing]);

	const update = (id: string, patch: Partial<StorefrontBanner>) => {
		onChange(current => current.map(banner => (banner.id === id ? { ...banner, ...patch } : banner)));
	};

	const addBanner = () => {
		onChange(current => [
			...current,
			{
				id: newBannerId(),
				name: "",
				imageUrl: "",
				linkType: "product",
				linkValue: "",
				startAt: null,
				endAt: null,
			},
		]);
	};

	const uploadImage = async (bannerId: string, file?: File) => {
		if (!file) return;
		if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.size > 8 * 1024 * 1024) {
			toast({
				title: t("common.error"),
				description: t("company.settings.storefront.banners.invalidImage"),
				variant: "destructive",
			});
			return;
		}
		setUploadingIds(current => [...current, bannerId]);
		onUploadStart?.();
		try {
			const response = await companyAPI.uploadStorefrontBanner(file);
			update(bannerId, { imageUrl: response.url });
		} catch {
			toast({
				title: t("common.error"),
				description: t("company.settings.storefront.banners.uploadFailed"),
				variant: "destructive",
			});
		} finally {
			setUploadingIds(current => current.filter(id => id !== bannerId));
			onUploadEnd?.();
		}
	};

	return (
		<Card className="md:col-span-2">
			<CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<CardTitle className="text-base">{t("company.settings.storefront.banners.title")}</CardTitle>
					<p className="mt-1 text-sm text-muted-foreground">
						{t("company.settings.storefront.banners.description")}
					</p>
				</div>
				{editing ? (
					<Button type="button" variant="outline" onClick={addBanner}>
						<Plus className="me-2 h-4 w-4" />
						{t("company.settings.storefront.banners.add")}
					</Button>
				) : null}
			</CardHeader>
			<CardContent className="space-y-5">
				{banners.length === 0 ? (
					<div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
						<Image className="mx-auto mb-3 h-8 w-8 opacity-50" />
						{t("company.settings.storefront.banners.empty")}
					</div>
				) : null}

				{banners.map((banner, index) => {
					const uploading = uploadingIds.includes(banner.id);
					const destinationItems: Array<{ id: number; name: string }> =
						banner.linkType === "product" ? products : categories;
					const destinationName = destinationItems.find(item => String(item.id) === banner.linkValue)?.name;
					const destinationFallback = `${t(
						banner.linkType === "product"
							? "company.settings.storefront.banners.product"
							: "company.settings.storefront.banners.category",
					)} #${banner.linkValue}`;
					return (
						<div key={banner.id} className="border-t pt-4 first:border-t-0 first:pt-0">
							<div className="mb-4 flex items-center justify-between gap-3">
								<p className="font-medium">
									{banner.name.trim() ||
										t("company.settings.storefront.banners.untitled", { number: index + 1 })}
								</p>
								{editing ? (
									<div className="flex shrink-0 items-center gap-1">
										{[
											[-1, ArrowUp, "moveUp"],
											[1, ArrowDown, "moveDown"],
										].map(([direction, Icon, label]) => {
											const offset = Number(direction);
											const MoveIcon = Icon as typeof ArrowUp;
											return (
												<Button
													type="button"
													key={String(label)}
													variant="ghost"
													size="icon"
													disabled={
														!!uploadingIds.length ||
														index + offset < 0 ||
														index + offset >= banners.length
													}
													aria-label={`${t(`storefrontDesign.${label}`)}: ${banner.name}`}
													onClick={() =>
														onChange(current => {
															const next = [...current];
															[next[index], next[index + offset]] = [
																next[index + offset],
																next[index],
															];
															return next;
														})
													}
												>
													<MoveIcon className="h-4 w-4" aria-hidden />
												</Button>
											);
										})}
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() =>
												onChange(current => current.filter(item => item.id !== banner.id))
											}
											className="text-destructive hover:text-destructive"
										>
											<Trash2 className="me-2 h-4 w-4" />
											{t("company.settings.storefront.banners.remove")}
										</Button>
									</div>
								) : null}
							</div>

							<div className="grid gap-4 md:grid-cols-[144px_minmax(0,1fr)]">
								<div>
									<div className="aspect-[3/1] overflow-hidden rounded-lg border bg-muted">
										{banner.imageUrl ? (
											<img
												src={resolveImageUrl(banner.imageUrl)}
												alt={banner.name || t("company.settings.storefront.banners.preview")}
												className="h-full w-full object-cover"
											/>
										) : (
											<div className="flex h-full items-center justify-center text-xs text-muted-foreground">
												{t("company.settings.storefront.banners.ratioHint")}
											</div>
										)}
									</div>
									{editing ? (
										<Label className="relative mt-3 inline-flex cursor-pointer rounded-full has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
											<input
												type="file"
												accept="image/jpeg,image/png,image/webp,image/gif"
												className="sr-only"
												aria-label={t("company.settings.storefront.banners.upload")}
												disabled={uploading}
												onChange={event => void uploadImage(banner.id, event.target.files?.[0])}
											/>
											<span className="inline-flex min-h-11 items-center rounded-full border bg-background px-4 text-sm font-medium shadow-sm hover:bg-accent">
												{uploading ? (
													<Loader2 className="me-2 h-4 w-4 animate-spin" />
												) : (
													<Upload className="me-2 h-4 w-4" />
												)}
												{t("company.settings.storefront.banners.upload")}
											</span>
										</Label>
									) : null}
								</div>

								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2 sm:col-span-2">
										<Label htmlFor={`banner-name-${banner.id}`}>
											{t("company.settings.storefront.banners.name")}
										</Label>
										<Input
											id={`banner-name-${banner.id}`}
											value={banner.name}
											maxLength={120}
											readOnly={!editing}
											onChange={event => update(banner.id, { name: event.target.value })}
											placeholder={t("company.settings.storefront.banners.namePlaceholder")}
										/>
									</div>

									<div className="space-y-2">
										<Label>{t("company.settings.storefront.banners.linkType")}</Label>
										<Select
											value={banner.linkType}
											disabled={!editing}
											onValueChange={(value: StorefrontBannerLinkType) =>
												update(banner.id, { linkType: value, linkValue: "" })
											}
										>
											<SelectTrigger
												aria-label={t("company.settings.storefront.banners.linkType")}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="product">
													{t("company.settings.storefront.banners.product")}
												</SelectItem>
												<SelectItem value="category">
													{t("company.settings.storefront.banners.category")}
												</SelectItem>
												<SelectItem value="custom">
													{t("company.settings.storefront.banners.custom")}
												</SelectItem>
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-2">
										<Label>{t("company.settings.storefront.banners.destination")}</Label>
										{!editing ? (
											<Input
												aria-label={t("company.settings.storefront.banners.destination")}
												value={
													banner.linkType === "custom"
														? banner.linkValue
														: destinationName || destinationFallback
												}
												readOnly
												dir={banner.linkType === "custom" ? "ltr" : undefined}
											/>
										) : banner.linkType === "custom" ? (
											<Input
												aria-label={t("company.settings.storefront.banners.destination")}
												value={banner.linkValue}
												readOnly={!editing}
												dir="ltr"
												onChange={event => update(banner.id, { linkValue: event.target.value })}
												placeholder="https://example.com or /page"
											/>
										) : (
											<Select
												value={banner.linkValue || undefined}
												disabled={!editing || loadingTargets}
												onValueChange={value => update(banner.id, { linkValue: value })}
											>
												<SelectTrigger
													aria-label={t("company.settings.storefront.banners.destination")}
												>
													<SelectValue
														placeholder={
															loadingTargets
																? t("common.loading")
																: t(
																		"company.settings.storefront.banners.chooseDestination",
																	)
														}
													/>
												</SelectTrigger>
												<SelectContent>
													{destinationItems.map(item => (
														<SelectItem key={item.id} value={String(item.id)}>
															{item.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									</div>

									<div className="space-y-2">
										<Label htmlFor={`banner-start-${banner.id}`}>
											{t("company.settings.storefront.banners.startDate")}
										</Label>
										<Input
											id={`banner-start-${banner.id}`}
											type="date"
											value={toDateInput(banner.startAt)}
											readOnly={!editing}
											onChange={event =>
												update(banner.id, { startAt: fromDateInput(event.target.value, false) })
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor={`banner-end-${banner.id}`}>
											{t("company.settings.storefront.banners.endDate")}
										</Label>
										<Input
											id={`banner-end-${banner.id}`}
											type="date"
											value={toDateInput(banner.endAt)}
											readOnly={!editing}
											onChange={event =>
												update(banner.id, { endAt: fromDateInput(event.target.value, true) })
											}
										/>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}
